import { GeneratedAsset } from './types'
import { ILeaf, INode } from 'shared/types/tree'
import {
  idsForNode,
  updateAll,
  UpdateMethod,
  UpdateType,
} from '../nodeUpdates/utility'
import {
  contentFromNode,
  nodeForNewContentString,
} from '../htmlContentUpdates/utility'
import { parseHtmlString } from 'shared/utils'
import { AltTextByUrl, isValidAltText, UrlData } from '../utility/nodeIssues'
import { exportCsv } from 'shared/utils'
import { getImgElementsForNode, nodesWithImgElements } from '../utility/utility'
import { NodeBasedUpdates, UpdateData } from '../nodeUpdates/types'

export function getRemoveAltTextUpdates(altTextData: UrlData[]) {
  return altTextData.reduce<UpdateData[]>((acc, { nodes, leaves, url }) => {
    nodes.forEach((node) => {
      const updateData = {
        [url]: { url, altText: '', assetId: '' },
      }
      acc.push(...nodeUpdateData(node, [url], updateData).updates)
    })
    leaves.forEach((leaf) => {
      acc.push(...leafUpdateData(leaf).updates)
    })
    return acc
  }, [])
}

export function getAltTextUpdates(
  altTextData: UrlData[],
  generatedAssets: Record<string, GeneratedAsset>,
) {
  // This is all urls for each node id, we need to update all in one node update
  // otherwise we overwrite each other
  const urlsForNodes = altTextData.reduce<
    Record<string, { urls: string[]; node: INode }>
  >((acc, { nodes, url }) => {
    if (!(url in generatedAssets)) return acc
    nodes.forEach((node) => {
      if (!(node.id in acc)) {
        acc[node.id] = {
          node,
          urls: [],
        }
      }
      acc[node.id].urls.push(url)
    })
    return acc
  }, {})

  const nodeUpdates = Object.values(urlsForNodes).map(({ node, urls }) => {
    return nodeUpdateData(node, urls, generatedAssets)
  })

  const leafUpdates = altTextData.reduce<NodeBasedUpdates[]>(
    (acc, { leaves, url }) => {
      if (!(url in generatedAssets)) return acc
      const updateData = generatedAssets[url]
      leaves.forEach((leaf) => {
        acc.push(leafUpdateData(leaf, updateData))
      })
      return acc
    },
    [],
  )
  return [...nodeUpdates, ...leafUpdates]
}

// It's important we get all urls for the node update, if we try to do them sequentially
// we can have updates overwriting previous updates.
function nodeUpdateData(
  node: INode,
  urls: string[],
  generatedAssets: Record<string, GeneratedAsset>,
): NodeBasedUpdates {
  const content = contentFromNode(node)
  const asHtml = parseHtmlString(content)
  Array.from(asHtml.getElementsByTagName('img')).forEach((imgElm) => {
    const currentUrl = imgElm.src
    const generated = generatedAssets[currentUrl]
    if (!generated) return
    const { altText, assetId } = generated
    if (imgElm.alt === altText) return
    if (imgElm.alt && !imgElm.getAttribute('data-original-alt')) {
      imgElm.setAttribute('data-original-alt', imgElm.alt)
    }
    if (assetId) {
      imgElm.setAttribute('data-asset-id', assetId)
    }
    imgElm.alt = altText
  })
  const updateData = nodeForNewContentString(asHtml.body.innerHTML, node)
  return {
    node,
    updates: [
      {
        type: UpdateType.node,
        ids: idsForNode(node),
        method: UpdateMethod.update,
        params: updateData,
      },
    ],
  }
}

function leafUpdateData(
  leaf: ILeaf,
  { altText, assetId }: { altText?: string; assetId?: string } = {},
): NodeBasedUpdates {
  const body = JSON.parse(JSON.stringify(leaf.body))
  body.file.alt = altText
  body.altTextAssetId = assetId ?? body.altTextAssetId
  return {
    updates: [
      {
        type: UpdateType.leaf,
        ids: idsForNode(leaf),
        method: UpdateMethod.update,
        params: { body },
      },
    ],
  }
}

export function doUpdates(
  urls: string[],
  allUrlData: Record<string, UrlData>,
  assets: Record<string, GeneratedAsset>,
) {
  const urlDatas = urls.map((url) => allUrlData[url])
  const updates = getAltTextUpdates(urlDatas, assets)
  return updateAll(...updates)
}

export function doNodeUpdate(
  url: string,
  node: INode,
  allUrlData: Record<string, UrlData>,
  assets: Record<string, GeneratedAsset>,
) {
  const data = {
    ...allUrlData[url],
    nodes: [node],
    leaves: [],
  }
  const updates = getAltTextUpdates([data], assets)
  return updateAll(...updates)
}

export function doLeafUpdate(
  url: string,
  leaf: ILeaf,
  allUrlData: Record<string, UrlData>,
  assets: Record<string, GeneratedAsset>,
) {
  const data = {
    ...allUrlData[url],
    nodes: [],
    leaves: [leaf],
  }
  const updates = getAltTextUpdates([data], assets)
  return updateAll(...updates)
}

export function exportUrls(...urls: string[]) {
  const encoded = urls.map((url) => {
    if (url !== decodeURIComponent(url)) {
      return [url]
    }
    return [encodeURI(url)]
  })
  exportCsv([['url'], ...encoded])
}

export function nodeAltTextData(tree: INode) {
  const nodes = nodesWithImgElements(tree)
  return nodes.reduce<AltTextByUrl>((acc, node) => {
    const imgElms = getImgElementsForNode(node)
    imgElms.forEach((elm) => {
      const url = elm.getAttribute('src')
      const currAltText = elm.getAttribute('alt')
      if (!url) return acc
      const altText = isValidAltText({ altText: currAltText, url })
        ? currAltText
        : undefined
      if (url in acc) {
        acc[url].nodes.push(node)
      } else {
        acc[url] = {
          nodes: [node],
          leaves: [],
          url,
          altText,
        }
      }
    })
    return acc
  }, {})
}
